
import { useState, useEffect } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Rule } from "@/lib/types";
import { RuleItem } from "./RuleItem";
import { MoveVertical } from "lucide-react";
import { cn } from "@/lib/utils";
import { updateRuleOrder } from "@/services/ruleService";

interface SortableRuleItemProps {
  rule: Rule;
  onEdit: (rule: Rule) => void;
  onDelete: (rule: Rule) => void;
}

function SortableRuleItem({ rule, onEdit, onDelete }: SortableRuleItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "border-b last:border-0",
        isDragging ? "opacity-50 bg-muted" : ""
      )}
    >
      <div className="py-4 px-4 grid grid-cols-[20px_2fr_1fr_1fr_1fr_1fr] items-center gap-2">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab hover:text-primary mr-2"
          aria-label="Drag to reorder rule"
        >
          <MoveVertical className="h-4 w-4" />
        </div>
        <RuleItem 
          rule={rule} 
          onEdit={onEdit} 
          onDelete={onDelete} 
          disableDrag={true}
        />
      </div>
    </div>
  );
}

interface SortableRuleListProps {
  rules: Rule[];
  onEdit: (rule: Rule) => void;
  onDelete: (rule: Rule) => void;
  tenantId: string;
  onReorder?: (updatedRules: Rule[]) => void; // New prop to notify parent of reordering
}

export function SortableRuleList({
  rules,
  onEdit,
  onDelete,
  tenantId,
  onReorder
}: SortableRuleListProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [items, setItems] = useState<Rule[]>([]);
  const [isReordering, setIsReordering] = useState(false);

  // Update local state when rules prop changes
  useEffect(() => {
    setItems(rules);
  }, [rules]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: any) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setItems((prevItems) => {
        const oldIndex = prevItems.findIndex((item) => item.id === active.id);
        const newIndex = prevItems.findIndex((item) => item.id === over.id);
        
        // Create reordered array
        const newItems = arrayMove(prevItems, oldIndex, newIndex);
        
        // Set isReordering flag to show loading state
        setIsReordering(true);
        
        // Update the backend with new order
        const updateData = newItems.map((item, index) => ({
          id: item.id,
          order_number: index
        }));
        
        updateRuleOrder(updateData).then(() => {
          // Notify parent component of the reordering
          if (onReorder) {
            // Map the updated order to the full rule objects
            const updatedRules = newItems.map((item, index) => ({
              ...item,
              order: index  // Update the order property to match the new position
            }));
            onReorder(updatedRules);
          }
        }).finally(() => {
          setIsReordering(false);
        });
        
        return newItems;
      });
    }
    
    setActiveId(null);
  };

  return (
    <div className={cn("relative", isReordering && "opacity-70 pointer-events-none")}>
      {isReordering && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-10">
          <div className="text-sm text-muted-foreground">Updating rule order...</div>
        </div>
      )}
      <div className="">
        <div className="grid grid-cols-[20px_2fr_1fr_1fr_1fr_1fr] border-b px-4 py-3 font-medium">
          <div className="col-span-2">Name</div>
          <div>Conditions</div>
          <div>Actions</div>
          <div className="col-span-2">Status</div>
        </div>
        
        <DndContext 
          sensors={sensors} 
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={items.map(rule => rule.id)} 
            strategy={verticalListSortingStrategy}
          >
            {items.map((rule) => (
              <SortableRuleItem
                key={rule.id}
                rule={rule}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}
